import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSessionState } from '../hooks/useSessionState';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentModule } from '../contexts/ModuleContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { MessageSquareText, Search, Star, Filter, X, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RatingStars = ({ rating }) => (
  <div className="flex items-center gap-0.5" data-testid={`rating-${rating}`}>
    {[1, 2, 3, 4, 5].map(i => (
      <Star
        key={i}
        className={`w-4 h-4 ${i <= rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`}
      />
    ))}
    <span className="ml-1.5 text-sm font-medium text-muted-foreground">{rating}/5</span>
  </div>
);

const MultiSelectFilter = ({ label, options, selected, onToggle, icon: Icon }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = options.filter(o =>
    o.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`gap-1.5 h-9 ${selected.length > 0 ? 'border-primary text-primary' : ''}`}
          data-testid={`filter-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {Icon && <Icon className="w-3.5 h-3.5" />}
          {label}
          {selected.length > 0 && (
            <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs h-5 rounded-full">
              {selected.length}
            </Badge>
          )}
          <ChevronDown className="w-3.5 h-3.5 ml-0.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        {options.length > 5 && (
          <Input
            placeholder={`Search ${label.toLowerCase()}...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="mb-2 h-8 text-sm"
          />
        )}
        <div className="max-h-48 overflow-y-auto space-y-0.5">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2">No options</p>
          ) : (
            filtered.map(option => (
              <label
                key={option}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm"
              >
                <Checkbox
                  checked={selected.includes(option)}
                  onCheckedChange={() => onToggle(option)}
                />
                <span className="truncate">{option}</span>
              </label>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function FeedbackPage() {
  const navigate = useNavigate();
  const { getAuthHeader, employee } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useSessionState('feedback:search', '');

  // Multi-select filter states (persisted)
  const [selectedRequesters, setSelectedRequesters] = useSessionState('feedback:requesters', []);
  const [selectedRatings, setSelectedRatings] = useSessionState('feedback:ratings', []);
  const [selectedAssignees, setSelectedAssignees] = useSessionState('feedback:assignees', []);
  const [moduleFilter, setModuleFilter] = useSessionState('feedback:module', 'Admin'); // Admin | IT

  // Sync moduleFilter -> global header
  const { setCurrentModule } = useCurrentModule();
  useEffect(() => { setCurrentModule(moduleFilter); }, [moduleFilter, setCurrentModule]);

  // Role guard
  const role = employee?.role || 'Employee';
  const hasAccess = role === 'Management' || role === 'Super Admin';

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/feedback/tickets`, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setTickets(data);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to load feedback data');
      }
    } catch {
      toast.error('Failed to load feedback data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (hasAccess) fetchFeedback();
  }, [fetchFeedback, hasAccess]);

  // Derive unique filter options (scoped to current module so IT/Admin slicer shows correct assignees)
  const filterOptions = useMemo(() => {
    const moduleTickets = tickets.filter(t => (t.module || 'Admin') === moduleFilter);
    const requesters = [...new Set(moduleTickets.map(t => t.requester_name).filter(Boolean))].sort();
    const assignees = [...new Set(
      moduleTickets.map(t => moduleFilter === 'IT' ? t.assigned_it_name : t.assigned_admin_name).filter(Boolean)
    )].sort();
    const ratings = ['1', '2', '3', '4', '5'];
    return { requesters, assignees, ratings };
  }, [tickets, moduleFilter]);

  // Apply all filters
  const filteredTickets = useMemo(() => {
    return tickets.filter(t => {
      // Search
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const type = t.request_type === 'Other' ? t.other_request_type : t.request_type;
        const matchSearch =
          (t.ticket_id || '').toLowerCase().includes(q) ||
          (t.requester_name || '').toLowerCase().includes(q) ||
          (type || '').toLowerCase().includes(q);
        if (!matchSearch) return false;
      }
      // Multi-select filters
      if (selectedRequesters.length > 0 && !selectedRequesters.includes(t.requester_name)) return false;
      if (selectedRatings.length > 0 && !selectedRatings.includes(String(t.rating))) return false;
      if (selectedAssignees.length > 0) {
        const assigneeName = (t.module || 'Admin') === 'IT' ? t.assigned_it_name : t.assigned_admin_name;
        if (!selectedAssignees.includes(assigneeName)) return false;
      }
      if ((t.module || 'Admin') !== moduleFilter) return false;
      return true;
    });
  }, [tickets, searchQuery, selectedRequesters, selectedRatings, selectedAssignees, moduleFilter]);

  const toggleFilter = (setter) => (value) => {
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const activeFilterCount = selectedRequesters.length + selectedRatings.length + selectedAssignees.length;

  const clearAllFilters = () => {
    setSelectedRequesters([]);
    setSelectedRatings([]);
    setSelectedAssignees([]);
    setSearchQuery('');
  };

  if (!hasAccess) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-muted-foreground">You don't have access to this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="feedback-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Feedback</h1>
          <p className="text-muted-foreground mt-1">
            Review ratings and feedback on completed tickets
          </p>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquareText className="w-5 h-5" />
                  Ticket Feedback
                </CardTitle>
                <CardDescription>
                  {filteredTickets.length} rated ticket{filteredTickets.length !== 1 ? 's' : ''}
                  {activeFilterCount > 0 && ` (filtered from ${tickets.length})`}
                </CardDescription>
              </div>
            </div>

            {/* Search + Filters row */}
            <div className="flex flex-wrap items-center gap-2 pt-3">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search ID, requester, type..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="feedback-search"
                />
              </div>

              <MultiSelectFilter
                label="Requester"
                options={filterOptions.requesters}
                selected={selectedRequesters}
                onToggle={toggleFilter(setSelectedRequesters)}
              />
              <MultiSelectFilter
                label="Rating"
                options={filterOptions.ratings}
                selected={selectedRatings}
                onToggle={toggleFilter(setSelectedRatings)}
                icon={Star}
              />
              <MultiSelectFilter
                label="Assigned To"
                options={filterOptions.assignees}
                selected={selectedAssignees}
                onToggle={toggleFilter(setSelectedAssignees)}
              />

              {/* Department / Module slicer */}
              <div className="flex items-center gap-1 border rounded-md p-1 bg-card h-9" data-testid="feedback-module-slicer">
                {[
                  { v: 'Admin', l: 'Admin' },
                  { v: 'IT', l: 'IT' },
                ].map(opt => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setModuleFilter(opt.v)}
                    data-testid={`feedback-module-${opt.v.toLowerCase()}`}
                    className={`px-2.5 py-0.5 text-xs rounded font-medium transition ${
                      moduleFilter === opt.v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>

              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 gap-1 text-muted-foreground hover:text-destructive"
                  onClick={clearAllFilters}
                  data-testid="clear-all-filters"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear All
                </Button>
              )}
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <MessageSquareText className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No feedback found</p>
                <p className="text-sm mt-1">
                  {activeFilterCount > 0 || searchQuery
                    ? 'Try adjusting your filters or search'
                    : 'Completed tickets with ratings will appear here'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Ticket ID</TableHead>
                      <TableHead>Request Type</TableHead>
                      <TableHead>Requester</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Feedback Rating</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTickets.map(ticket => (
                      <TableRow key={ticket.ticket_id} className="table-row-hover">
                        <TableCell>
                          <button
                            onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                            className="ticket-id-link font-mono text-sm"
                            data-testid={`feedback-ticket-${ticket.ticket_id}`}
                          >
                            {ticket.ticket_id}
                          </button>
                        </TableCell>
                        <TableCell className="font-medium max-w-[180px] truncate">
                          {ticket.request_type === 'Other'
                            ? ticket.other_request_type
                            : ticket.request_type}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {ticket.requester_name}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {((ticket.module || 'Admin') === 'IT' ? ticket.assigned_it_name : ticket.assigned_admin_name) || '-'}
                        </TableCell>
                        <TableCell>
                          <RatingStars rating={ticket.rating} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
